import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ShieldCheck, Loader2, History, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SUPPORTED_ASSETS } from "@/lib/constants";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import AmlReportCard from "@/components/AmlReportCard";

const PRICE = 2;

// Валидаторы адреса по сети — чтобы не тратить платную проверку на битый адрес.
const VALIDATORS = {
  TRC20: /^T[1-9A-HJ-NP-Za-km-z]{33}$/,
  ERC20: /^0x[a-fA-F0-9]{40}$/,
  TON: /^(EQ|UQ|kQ|0Q)[A-Za-z0-9_-]{46}$/,
  SOL: /^[1-9A-HJ-NP-Za-km-z]{32,44}$/,
  BTC: /^(bc1[ac-hj-np-z02-9]{11,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,39})$/,
};

// Уверенное автоопределение сети по префиксу/формату адреса.
const detectNetwork = (addr) => {
  const a = (addr || "").trim();
  if (/^0x[a-fA-F0-9]{40}$/.test(a)) return "ERC20";
  if (/^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(a)) return "TRC20";
  if (/^(EQ|UQ|kQ|0Q)[A-Za-z0-9_-]{46}$/.test(a)) return "TON";
  if (/^(bc1[ac-hj-np-z02-9]{11,87}|[13][a-km-zA-HJ-NP-Z1-9]{25,39})$/.test(a)) return "BTC";
  return null;
};

const AmlCheckPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, balances, refreshData, haptic, isTelegramWebApp } = useApp();
  const showInlineBack = !isTelegramWebApp;

  const [step, setStep] = useState("input"); // input | report
  const [asset, setAsset] = useState("USDT:TRC20");
  const [address, setAddress] = useState("");
  const [txHash, setTxHash] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState(null);

  const assetOptions = useMemo(
    () =>
      SUPPORTED_ASSETS.map((a) => ({
        key: `${a.currency}:${a.network}`,
        label: `${a.currency} · ${a.network}`,
        currency: a.currency,
        network: a.network,
      })),
    []
  );
  const selected = assetOptions.find((o) => o.key === asset) || assetOptions[0];

  const usdtAvailable = useMemo(() => {
    const row = (balances || []).find((b) => String(b?.currency || "").toUpperCase() === "USDT");
    return Number(row?.available ?? (Number(row?.amount || 0) - Number(row?.hold || 0))) || 0;
  }, [balances]);
  const insufficient = usdtAvailable < PRICE;

  const addr = address.trim();
  const validator = VALIDATORS[selected.network.toUpperCase()];
  const addrValid = addr.length === 0 ? null : validator ? validator.test(addr) : addr.length >= 5;
  const addrError = addrValid === false;

  const onAddrChange = (val) => {
    setAddress(val);
    const det = detectNetwork(val.trim());
    if (det && det !== selected.network.toUpperCase()) {
      const opt = assetOptions.find((o) => o.network.toUpperCase() === det);
      if (opt) setAsset(opt.key);
    }
  };

  const canSubmit = !loading && addr.length >= 5 && !addrError && !insufficient;

  const runCheck = async () => {
    haptic("light");
    setLoading(true);
    try {
      const res = await api.post("/aml/check", {
        address: addr,
        network: selected.network.toLowerCase(),
        coin: selected.currency,
        direction: "DEPOSIT",
        tx_hash: txHash.trim() || null,
      });
      setResult(res.data);
      setStep("report");
      haptic("success");
      refreshData?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        toast.error(detail || t("aml.err_balance") || "Недостаточно средств");
      } else {
        toast.error(detail || t("aml.err_generic") || "Не удалось выполнить проверку");
      }
      haptic("error");
    } finally {
      setLoading(false);
    }
  };

  if (step === "report" && result) {
    return (
      <div className="px-4 pb-4 pt-[calc(1rem+var(--lp-top))] safe-bottom animate-fade-in" data-testid="aml-report">
        <p className="text-center text-xs font-semibold tracking-widest text-muted-foreground mb-3">
          2 · {t("aml.report") || "ОТЧЁТ"}
        </p>
        <AmlReportCard
          result={result}
          onBack={() => { setStep("input"); haptic("light"); }}
          onCheckAnother={() => { setResult(null); setAddress(""); setTxHash(""); setStep("input"); haptic("light"); }}
        />
      </div>
    );
  }

  return (
    <div className="p-4 safe-bottom animate-fade-in" data-testid="aml-check-page">
      <div className="flex items-center justify-between mb-4">
        {showInlineBack ? (
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t("common.back") || "Назад"}
          </button>
        ) : <span />}
        <button
          onClick={() => navigate("/aml-history")}
          className="flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <History className="w-4 h-4 mr-1" />
          {t("aml.history") || "Мои проверки"}
        </button>
      </div>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-11 h-11 rounded-full bg-blue-50 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-link dark:text-blue-900" />
        </div>
        <div>
          <h1 className="text-xl font-bold">{t("aml.title") || "AML-проверка"}</h1>
          <p className="text-xs text-muted-foreground">
            {t("aml.subtitle") || "Проверьте кошелёк на связь с рисковыми источниками"}
          </p>
        </div>
      </div>

      <div className="lp-glass p-4 mt-4 space-y-4">
        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t("aml.network") || "Сеть и актив"}
          </label>
          <Select value={asset} onValueChange={setAsset}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-[194px]">
              {assetOptions.map((o) => (
                <SelectItem key={o.key} value={o.key}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t("aml.address") || "Адрес кошелька"}
          </label>
          <Input
            className={`mt-1 ${addrError ? "border-red-500 focus-visible:ring-red-500" : ""}`}
            placeholder={t("aml.address_ph") || "Вставьте адрес"}
            value={address}
            onChange={(e) => onAddrChange(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
          {addrError && (
            <p className="mt-1 text-[11px] text-red-500">
              {t("aml.err_address_fmt") || `Не похоже на адрес сети ${selected.network}`}
            </p>
          )}
        </div>

        <div>
          <label className="text-xs font-medium text-muted-foreground">
            {t("aml.txhash") || "Хэш транзакции (необязательно)"}
          </label>
          <Input
            className="mt-1"
            placeholder={t("aml.txhash_ph") || "TXID — если нужна проверка конкретной операции"}
            value={txHash}
            onChange={(e) => setTxHash(e.target.value)}
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
          />
        </div>

        {/* Цена и баланс */}
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">{t("aml.price") || "Стоимость"}</span>
          <span className="font-semibold">{PRICE} USDT</span>
        </div>
        <div className="flex items-center justify-between text-sm -mt-2">
          <span className="text-muted-foreground">{t("aml.balance") || "Баланс"}</span>
          <span className={insufficient ? "font-semibold text-red-500" : "font-semibold"}>
            {usdtAvailable.toFixed(2)} USDT
          </span>
        </div>

        {insufficient ? (
          <div className="rounded-xl bg-red-50 dark:bg-red-950/30 p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0" />
            <span className="text-xs text-red-600 dark:text-red-400">
              {t("aml.insufficient") || "Недостаточно средств для проверки"}
            </span>
            <button
              onClick={() => navigate("/topup")}
              className="ml-auto text-xs font-semibold text-link underline whitespace-nowrap"
            >
              {t("aml.topup") || "Пополнить"}
            </button>
          </div>
        ) : (
          <Button className="w-full" onClick={() => setConfirmOpen(true)} disabled={!canSubmit}>
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                {(t("aml.check_btn") || "Проверить")} · {PRICE} USDT
              </>
            )}
          </Button>
        )}
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("aml.confirm_title") || "Подтвердите проверку"}</AlertDialogTitle>
            <AlertDialogDescription>
              {(t("aml.confirm_text") || `Проверка спишет ${PRICE} USDT с вашего баланса.`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel") || "Отмена"}</AlertDialogCancel>
            <AlertDialogAction onClick={runCheck}>
              {t("aml.confirm_ok") || "Проверить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AmlCheckPage;
